import React, { useMemo } from 'react';
import { Autocomplete, TextField, Box, Chip, Typography } from '@mui/material';

// Shared typeahead supplier picker. Used wherever the user needs to pick a
// supplier — PO entry, PO modal, invoices form/filter, payments form/filter.
// Keep the behaviour consistent everywhere by going through this one component.
//
// Props:
//   value         numeric supplier id, or '' / null for empty
//   onChange      (supplierId, supplierObj) — supplierId is id or ''
//   suppliers     pre-loaded list (each: { id, supplier_name, supplier_code,
//                 currency, is_intercompany, is_active })
//   label         display label (default 'Supplier')
//   required      boolean
//   disabled      boolean
//   size          'small' | 'medium' (default 'small')
//   helperText    optional helper text
//   includeAllOption  when true, exposes an "All suppliers" sentinel for filters
//                     (returns '' on selection)
//   placeholder   optional placeholder
const SupplierPicker = ({
  value,
  onChange,
  suppliers = [],
  label = 'Supplier',
  required = false,
  disabled = false,
  size = 'small',
  helperText,
  includeAllOption = false,
  placeholder,
  sx,
}) => {
  const ALL_SENTINEL = useMemo(() => ({ id: '__ALL__', supplier_name: 'All Suppliers', __all: true }), []);

  const options = useMemo(() => {
    const sorted = (suppliers || [])
      .filter(s => s && s.supplier_name)
      .slice()
      .sort((a, b) => String(a.supplier_name).localeCompare(String(b.supplier_name)));
    return includeAllOption ? [ALL_SENTINEL, ...sorted] : sorted;
  }, [suppliers, includeAllOption, ALL_SENTINEL]);

  const selected = useMemo(() => {
    if (value === '' || value === null || value === undefined) {
      return includeAllOption ? ALL_SENTINEL : null;
    }
    return options.find(o => o.id === value) || null;
  }, [value, options, includeAllOption, ALL_SENTINEL]);

  return (
    <Autocomplete
      size={size}
      fullWidth
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_, picked) => {
        if (!picked || picked.__all) {
          onChange('', null);
        } else {
          onChange(picked.id, picked);
        }
      }}
      getOptionLabel={(opt) => (opt && opt.supplier_name) ? opt.supplier_name : ''}
      isOptionEqualToValue={(opt, val) => opt && val && opt.id === val.id}
      filterOptions={(opts, state) => {
        const q = (state.inputValue || '').trim().toLowerCase();
        if (!q) return opts;
        return opts.filter(o => {
          if (o.__all) return false; // hide "All" once user types
          const name = (o.supplier_name || '').toLowerCase();
          const code = (o.supplier_code || '').toLowerCase();
          return name.includes(q) || code.includes(q);
        });
      }}
      renderOption={(props, opt) => {
        if (opt.__all) {
          return (
            <li {...props} key="__ALL__">
              <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>All Suppliers</Typography>
            </li>
          );
        }
        return (
          <li {...props} key={opt.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {opt.supplier_name}
                </Typography>
                {opt.supplier_code && (
                  <Typography variant="caption" color="text.secondary">{opt.supplier_code}</Typography>
                )}
              </Box>
              {opt.currency && (
                <Chip label={opt.currency} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              )}
              {opt.is_intercompany ? (
                <Chip label="IC" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              ) : null}
              {opt.is_active === 0 ? (
                <Chip label="inactive" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
              ) : null}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={placeholder || 'Type to search…'}
          helperText={helperText}
        />
      )}
      sx={sx}
    />
  );
};

export default SupplierPicker;
